import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Info, Layers, Settings } from "lucide-react";
import { FC } from "react";
import { ErrorContent } from "@/components/IAndI/SummaryMetrics/ErrorContent/ErrorContent";
import { NotFoundContent } from "@/components/IAndI/SummaryMetrics/NotFoundContent";
import { env } from "@/config/env";

const SystemOverviewSkeleton: FC = () => (
	<div className="flex flex-col items-center gap-4">
		<Skeleton className="h-12 w-40 rounded-md bg-blue-50 mt-5" />
		<Skeleton className="h-4 w-64 rounded-md bg-blue-50" />
		<Skeleton className="h-10 w-full rounded-md bg-blue-50" />
	</div>
);

interface BlockageSystemOverviewCardProps {
	loading: boolean;
	loaded: boolean;
	error: ApiError | null;
	blockages: Dryp.Blockage[];
	structures: Dryp.Structure[];
}

export const BlockageSystemOverviewCard: FC<BlockageSystemOverviewCardProps> = ({
	loading,
	loaded,
	error,
	blockages,
	structures,
}) => {
	// eslint-disable-next-line eqeqeq -- type.id is a number from API, config key is a string
	const blockageIds = new Set(blockages.map(b => b.structureId));
	const monitoredCount = blockageIds.size;
	const unconfiguredCount = structures.filter(s => !blockageIds.has(s.id)).length;
	const structuresCount = monitoredCount + unconfiguredCount;
	const isNoData = loaded && !error && structuresCount === 0;
	const notConfiguredStructure = structures.find(s => !blockageIds.has(s.id));
	const url_env = env.mode === "production" ? "data" : "test";
	const target = notConfiguredStructure
		? `https://${url_env}.dryp.live/inside/sensor-manager?point=${encodeURIComponent(
				String(notConfiguredStructure.id),
			)}&tab=installation`
		: `https://${url_env}.dryp.live/inside/sensor-manager?tab=installation`;

	return (
		<Card className="shadow-sm border border-muted rounded-xl flex flex-col h-full drop-shadow-sm">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold flex items-center gap-2">
					<Layers className="h-4 w-4 text-purple-500" />
					System Overview
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-1 flex flex-col justify-center">
				{loading ? (
					<SystemOverviewSkeleton />
				) : isNoData ? (
					<NotFoundContent message="No monitoring data available" />
				) : error ? (
					<ErrorContent
						error={error}
						notFoundMessage="No monitoring data available"
						message="Unable to load monitoring statistics"
					/>
				) : (
					<>
						<div className="text-center mb-3">
							<div className="text-3xl font-bold text-primary leading-tight">{monitoredCount}</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								of <span className="text-foreground font-semibold">{structuresCount}</span> structures
								monitored
							</div>
						</div>

						{unconfiguredCount > 0 && (
							<div className="relative bg-muted/50 p-2.5 rounded-md text-[11px] text-muted-foreground mb-2">
								<Info className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
								<p className="text-center font-medium pl-6">
									{unconfiguredCount} structures are not yet configured for blockage detection
								</p>
							</div>
						)}

						<Button
							asChild
							size="sm"
							className="w-full text-xs font-medium"
							disabled={!notConfiguredStructure}
						>
							<a href={target} target="_blank" rel="noopener noreferrer">
								<Settings className="mr-2 h-4 w-4" />
								Configure Structures
							</a>
						</Button>
					</>
				)}
			</CardContent>
		</Card>
	);
};
